import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, UserCheck, UserX } from 'lucide-react';
import { useState } from 'react';
import toast from 'react-hot-toast';

import { apiGet, apiPost, apiPut } from '@/api/client';
import { endpoints } from '@/api/endpoints';
import { Button } from '@/components/ui/Button';
import { Input, Select } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Money } from '@/components/ui/Money';
import { PageHeader } from '@/components/ui/PageHeader';
import { Pill } from '@/components/ui/Pill';
import { ErrorState } from '@/components/ui/States';
import { DataTable } from '@/components/ui/Table';
import { fmtPhone } from '@/utils/format';

interface EmployeeRow {
  id: number;
  employee_code: string;
  user?: { id: number; name: string; phone: string; is_active: boolean };
  branch?: { id: number; name: string };
  role: string;
  salary_type: 'monthly' | 'daily';
  base_salary: number;
  commission_percent?: number;
  is_active: boolean;
}

interface BranchOpt {
  id: number;
  name: string;
}

interface EmpForm {
  name: string;
  phone: string;
  role: 'cashier' | 'stock_boy' | 'manager';
  branch_id: number | '';
  salary_type: 'monthly' | 'daily';
  base_salary: string;
  commission_percent: string;
  pin: string;
}

/**
 * Employees list — owner only. The mobile app has the full
 * attendance/punch flow; on web we focus on **management**: who's on
 * the team, their salary, role, branch. Salary processing happens via
 * the per-row action menu — calls the same `/employees/{id}/process-salary`
 * the Flutter app does.
 */
export function EmployeesListPage() {
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<EmployeeRow | null>(null);
  const [form, setForm] = useState<EmpForm>(blankForm());

  const list = useQuery({
    queryKey: ['employees'],
    queryFn: () =>
      apiGet<{ items: EmployeeRow[] } | EmployeeRow[]>(endpoints.employees),
  });

  const branches = useQuery({
    queryKey: ['branches'],
    queryFn: () =>
      apiGet<{ items: BranchOpt[] } | BranchOpt[]>(endpoints.branches),
  });
  const branchOpts: BranchOpt[] = Array.isArray(branches.data)
    ? branches.data
    : branches.data?.items ?? [];

  const rows = Array.isArray(list.data) ? list.data : list.data?.items ?? [];

  const saveMut = useMutation({
    mutationFn: async (f: EmpForm) => {
      // Client-side validation that matches the backend's `required`
      // rules. Doing it here gives the user a useful inline message
      // instead of a generic "validation failed" toast.
      if (!f.name.trim()) throw new Error('Name is required');
      if (!f.role) throw new Error('Role is required');
      if (!f.branch_id) throw new Error('Pick a branch');
      if (!f.salary_type) throw new Error('Pick a salary type');
      const baseSalary = Number(f.base_salary);
      if (!Number.isFinite(baseSalary) || baseSalary < 0)
        throw new Error('Base salary must be a non-negative number');

      // The CREATE endpoint takes (name, phone, role, branch_id,
      // salary_type, base_salary, commission_percent, pin). The UPDATE
      // endpoint takes a SUBSET (name, role, branch_id, salary_type,
      // base_salary, commission_percent, is_active) — phone is locked
      // (it's the login identifier) and PIN is reset elsewhere.
      // Sending phone/pin to update would either be ignored or trigger
      // unique-constraint errors. Build per-mode bodies.
      if (editing) {
        const updateBody = {
          name: f.name.trim(),
          role: f.role,
          branch_id: Number(f.branch_id),
          salary_type: f.salary_type,
          base_salary: baseSalary,
          commission_percent: f.commission_percent
            ? Number(f.commission_percent)
            : 0,
          is_active: true,
        };
        return apiPut(endpoints.employee(editing.id), updateBody);
      }

      // CREATE — phone + PIN are required only here.
      if (!/^[0-9]{10}$/.test(f.phone.trim()))
        throw new Error('Enter a 10-digit phone number');
      const createBody = {
        name: f.name.trim(),
        phone: f.phone.trim(),
        role: f.role,
        branch_id: Number(f.branch_id),
        salary_type: f.salary_type,
        base_salary: baseSalary,
        commission_percent: f.commission_percent
          ? Number(f.commission_percent)
          : 0,
        pin: f.pin.trim() || undefined,
      };
      return apiPost(endpoints.employees, createBody);
    },
    onSuccess: () => {
      toast.success(editing ? 'Saved' : 'Employee added');
      qc.invalidateQueries({ queryKey: ['employees'] });
      closeForm();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const closeForm = () => {
    setShowForm(false);
    setEditing(null);
    setForm(blankForm());
  };

  const onAdd = () => {
    setEditing(null);
    setForm(blankForm());
    setShowForm(true);
  };
  const onEdit = (r: EmployeeRow) => {
    setEditing(r);
    // Defensive defaults: legacy rows in the wild can have a missing
    // `salary_type` or `base_salary`. Without these defaults the form
    // would send `salary_type: undefined`, JSON.stringify would drop
    // the key, and the backend's `required|in:monthly,daily` rule
    // would reject the update with "salary type field is required".
    setForm({
      name: r.user?.name ?? '',
      phone: (r.user?.phone ?? '').replace(/^\+91/, ''),
      role: (r.role as EmpForm['role']) ?? 'cashier',
      branch_id: r.branch?.id ?? '',
      salary_type: r.salary_type ?? 'monthly',
      base_salary: String(r.base_salary ?? '0'),
      commission_percent: String(r.commission_percent ?? ''),
      pin: '',
    });
    setShowForm(true);
  };

  return (
    <>
      <PageHeader
        title="Employees"
        description="Your team — roles, salaries, and primary branch."
        actions={
          <Button iconLeft={<Plus className="h-4 w-4" />} onClick={onAdd}>
            Add employee
          </Button>
        }
      />

      {list.isError ? (
        <ErrorState
          title="Couldn't load employees"
          description={(list.error as Error)?.message}
          onRetry={() => list.refetch()}
        />
      ) : (
        <DataTable
          rowKey={(r) => r.id}
          loading={list.isLoading}
          rows={rows}
          onRowClick={onEdit}
          empty="No employees yet — your shop is a one-person show!"
          columns={[
            {
              key: 'name',
              header: 'Name',
              cell: (r) => (
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-full bg-brand-500/10 text-brand-700 grid place-items-center font-semibold text-sm">
                    {(r.user?.name ?? '?').charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div className="font-medium">{r.user?.name}</div>
                    <div className="text-xs text-ink-soft">
                      {fmtPhone(r.user?.phone)} · {r.employee_code}
                    </div>
                  </div>
                </div>
              ),
            },
            {
              key: 'role',
              header: 'Role',
              hideOnMobile: true,
              cell: (r) => <span className="capitalize">{r.role}</span>,
            },
            {
              key: 'branch',
              header: 'Branch',
              hideOnMobile: true,
              cell: (r) => r.branch?.name ?? '—',
            },
            {
              key: 'salary',
              header: 'Salary',
              className: 'text-right',
              cell: (r) => (
                <div className="text-right">
                  <Money value={r.base_salary} className="font-semibold" />
                  <div className="text-xs text-ink-soft capitalize">
                    {r.salary_type}
                  </div>
                </div>
              ),
            },
            {
              key: 'status',
              header: 'Status',
              cell: (r) =>
                r.is_active ? (
                  <Pill tone="success">
                    <UserCheck className="h-3 w-3" /> Active
                  </Pill>
                ) : (
                  <Pill tone="neutral">
                    <UserX className="h-3 w-3" /> Inactive
                  </Pill>
                ),
            },
          ]}
        />
      )}

      {/* Form modal */}
      <Modal
        open={showForm}
        onClose={closeForm}
        size="lg"
        title={editing ? 'Edit employee' : 'Add employee'}
        footer={
          <>
            <Button variant="outline" onClick={closeForm}>
              Cancel
            </Button>
            <Button
              loading={saveMut.isPending}
              onClick={() => saveMut.mutate(form)}
            >
              Save
            </Button>
          </>
        }
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Input
            name="name"
            label="Full name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <Input
            name="phone"
            // Phone is the login identifier on the backend, so the
            // update endpoint refuses changes. Lock the field on edit.
            label={editing ? 'Phone (locked)' : 'Phone (10 digits)'}
            inputMode="numeric"
            maxLength={10}
            value={form.phone}
            disabled={!!editing}
            hint={editing ? 'Phone cannot be changed after creation.' : undefined}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
          />
          <Select
            name="role"
            label="Role"
            value={form.role}
            onChange={(e) =>
              setForm({ ...form, role: e.target.value as EmpForm['role'] })
            }
          >
            <option value="cashier">Cashier</option>
            <option value="stock_boy">Stock boy</option>
            <option value="manager">Manager</option>
          </Select>
          <Select
            name="branch_id"
            label="Primary branch"
            value={String(form.branch_id)}
            onChange={(e) =>
              setForm({ ...form, branch_id: Number(e.target.value) })
            }
          >
            <option value="">Choose branch…</option>
            {branchOpts.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </Select>
          <Select
            name="salary_type"
            label="Salary type"
            value={form.salary_type}
            onChange={(e) =>
              setForm({
                ...form,
                salary_type: e.target.value as 'monthly' | 'daily',
              })
            }
          >
            <option value="monthly">Monthly</option>
            <option value="daily">Daily</option>
          </Select>
          <Input
            name="base_salary"
            label="Base salary (₹)"
            type="number"
            value={form.base_salary}
            onChange={(e) => setForm({ ...form, base_salary: e.target.value })}
          />
          <Input
            name="commission_percent"
            label="Commission % (optional)"
            type="number"
            value={form.commission_percent}
            onChange={(e) =>
              setForm({ ...form, commission_percent: e.target.value })
            }
          />
          <Input
            name="pin"
            // PIN is set only on creation. To change an existing
            // employee's PIN, ask them to use the mobile app's
            // "Forgot PIN" / Security flow — the update endpoint
            // doesn't take a PIN field.
            label={editing ? 'PIN (set on create)' : '4-digit PIN'}
            inputMode="numeric"
            maxLength={4}
            value={form.pin}
            disabled={!!editing}
            onChange={(e) =>
              setForm({ ...form, pin: e.target.value.replace(/\D/g, '') })
            }
            hint={
              editing
                ? "PIN reset isn't supported here yet. The employee can change it from the mobile app."
                : undefined
            }
          />
        </div>
      </Modal>
    </>
  );
}

function blankForm(): EmpForm {
  return {
    name: '',
    phone: '',
    role: 'cashier',
    branch_id: '',
    salary_type: 'monthly',
    base_salary: '',
    commission_percent: '',
    pin: '',
  };
}
